/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { css } from "@emotion/css";
import { DeprecatedDatePicker } from "@octopusdeploy/design-system-components";
import { DeprecatedSelect } from "@octopusdeploy/design-system-octopus-components";
import { space } from "@octopusdeploy/design-system-tokens";
import type { EnvironmentResource, TenantResource } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import { TaskDateFilterTypeValues, TaskFilterStateValues, type TaskDateFilterType, type TaskFilterState } from "@octopusdeploy/portal-routes";
import moment from "moment";
import { AdvancedFilterCheckbox } from "~/components/AdvancedFilterLayout";
import { Feature, FeatureToggle } from "~/components/FeatureToggle";
import { EnvironmentMultiSelect } from "~/components/MultiSelect/EnvironmentMultiSelect";
import { TenantMultiSelect } from "~/components/MultiSelect/TenantMultiSelect";
import { PermissionCheck } from "~/components/PermissionCheck";
export interface RunbookTaskFilter {
    ids?: string[];
    state?: TaskFilterState;
    environments?: string[];
    tenants?: string[];
    hasPendingInterruptions: boolean;
    hasWarningsOrErrors: boolean;
    dateFilterType?: TaskDateFilterType;
    fromDate?: Date;
    toDate?: Date;
}
interface RunbookRunsStateFilterProps {
    filter: RunbookTaskFilter;
    setFilter: (callback: (prev: RunbookTaskFilter) => RunbookTaskFilter) => void;
}
export function RunbookRunsStateFilter({ filter, setFilter }: RunbookRunsStateFilterProps) {
    const items = Object.keys(TaskFilterStateValues).map((t) => ({ value: t, text: t }));
    const styles = {
        states: css({
            width: "16rem",
        }),
    };
    return (<div className={styles.states}>
            <DeprecatedSelect value={filter.state} onChange={(value) => {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            const state = value as TaskFilterState | undefined;
            setFilter((prev) => ({ ...prev, state }));
        }} items={items} allowClear={true} fieldName="task state" placeholder="All task states"/>
        </div>);
}
interface RunbooksAdvancedFilterSectionProps {
    filter: RunbookTaskFilter;
    setFilter: (callback: (prev: RunbookTaskFilter) => RunbookTaskFilter) => void;
    environments?: EnvironmentResource[];
    tenants?: TenantResource[];
}
export function RunbooksAdvancedFilterSection({ filter, setFilter, environments, tenants }: RunbooksAdvancedFilterSectionProps) {
    const datePickerFormat = "MMM dd, yyyy";
    const fromError = filter.fromDate && filter.toDate && filter.fromDate > filter.toDate ? "Must be before the \"To\" date" : undefined;
    const toError = filter.fromDate && filter.toDate && filter.fromDate > filter.toDate ? "Must be after the \"From\" date" : undefined;
    const styles = {
        datepickerWrap: css({
            paddingBottom: space[16],
        }),
        checkboxFiltersContainer: css({
            marginBottom: space[16],
        }),
    };
    return (<>
            <div className={styles.checkboxFiltersContainer}>
                <AdvancedFilterCheckbox label="Awaiting manual intervention" value={filter.hasPendingInterruptions} onChange={(hasPendingInterruptions) => setFilter((prev) => ({ ...prev, hasPendingInterruptions }))}/>
                <AdvancedFilterCheckbox label="Has warnings or errors" value={filter.hasWarningsOrErrors} onChange={(hasWarningsOrErrors) => setFilter((prev) => ({ ...prev, hasWarningsOrErrors }))}/>
            </div>
            {environments && (<PermissionCheck permission={Permission.EnvironmentView} wildcard={true}>
                    <EnvironmentMultiSelect environments={environments} onChange={(environments) => setFilter((prev) => ({ ...prev, environments }))} value={filter.environments!}/>
                </PermissionCheck>)}
            {tenants && (<FeatureToggle feature={Feature.MultiTenancy}>
                    <PermissionCheck permission={Permission.TenantView} tenant="*">
                        <TenantMultiSelect items={tenants} onChange={(tenants) => setFilter((prev) => ({ ...prev, tenants }))} value={filter.tenants!}/>
                    </PermissionCheck>
                </FeatureToggle>)}
            <DeprecatedSelect value={filter.dateFilterType} allowFilter={false} sortItems={false} 
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    onChange={(dateFilterType) => setFilter((prev) => ({ ...filter, dateFilterType: dateFilterType as TaskDateFilterType }))} items={Object.values(TaskDateFilterTypeValues).map((v) => ({ value: v, text: v }))} allowClear={true} fieldName="date filter type" placeholder="Select a date type" label="By date"/>
            {filter.dateFilterType && (<>
                    <div className={styles.datepickerWrap}>
                        <DeprecatedDatePicker value={filter.fromDate!} onChange={(fromDate) => setFilter((prev) => ({ ...prev, fromDate }))} label="From" error={fromError} format={datePickerFormat}/>
                    </div>
                    <div className={styles.datepickerWrap}>
                        <DeprecatedDatePicker value={filter.toDate!} onChange={(toDate) => setFilter((prev) => ({ ...prev, toDate: moment(toDate).endOf("day").toDate() }))} label="To" error={toError} format={datePickerFormat}/>
                    </div>
                </>)}
        </>);
}
